/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/qaSelectionWrites.ts
 * FILE_ID: JOBHUB-QA-SELECTION-WRITES-001
 * PURPOSE: Apply QA selection delete, assign, and move onto the caches the screen reads.
 */
import type { QueryClient } from '@tanstack/react-query'
import type { QaApi, QaAreaExecution, QaChecklistInstance, QaItemInstance, QaMoveResult } from '../../qa/qaApi'
import {
  appendChecklist,
  holdQaArea,
  releaseQaArea,
  removeChecklist,
  removeItems,
  withAssignee,
} from './qaExecutionCache'
import { isPendingQaChange } from './qaTabSelectors'

export type QaDeleteTargets = { items: string[]; checklists: string[]; areas: string[] }

export type QaDeleteResult = {
  deletedItems: string[]
  pendingItems: string[]
  deletedChecklists: string[]
  deletedAreas: string[]
  failed: number
}

type AreaRow = { id: string; floor?: string; sort_order?: number; area_name?: string }
type FloorRow = { id: string; inspection?: string; floor_name?: string; areas?: AreaRow[] }

function rowsOf<T>(current: unknown): T[] {
  if (Array.isArray(current)) return current as T[]
  if (current && typeof current === 'object' && Array.isArray((current as { results?: T[] }).results)) {
    return (current as { results: T[] }).results
  }
  return []
}

function mapCachedList<T>(current: unknown, mapRows: (rows: T[]) => T[]): unknown {
  if (Array.isArray(current)) return mapRows(current as T[])
  if (current && typeof current === 'object' && Array.isArray((current as { results?: unknown }).results)) {
    const page = current as { results: T[] }
    return { ...page, results: mapRows(page.results) }
  }
  return current
}

function mapsOf(executions: readonly QaAreaExecution[]) {
  const itemArea = new Map<string, string>()
  const checklistArea = new Map<string, string>()
  for (const area of executions) {
    for (const checklist of area.checklists ?? []) {
      checklistArea.set(checklist.id, area.id)
      for (const item of checklist.items ?? []) itemArea.set(item.id, area.id)
    }
  }
  return { itemArea, checklistArea }
}

function putItemBack(current: QaAreaExecution, saved: QaAreaExecution, itemId: string): QaAreaExecution {
  const original = saved.checklists.find((checklist) => checklist.items.some((item) => item.id === itemId))
  const item = original?.items.find((row) => row.id === itemId)
  if (!original || !item) return current
  if (current.checklists.some((checklist) => checklist.items.some((row) => row.id === itemId))) return current
  const index = original.items.findIndex((row) => row.id === itemId)
  return removeItems({
    ...current,
    checklists: current.checklists.map((checklist) => {
      if (checklist.id !== original.id) return checklist
      const items = [...checklist.items]
      items.splice(Math.max(0, index), 0, item)
      return { ...checklist, items }
    }),
  }, [])
}

function putChecklistBack(current: QaAreaExecution, saved: QaAreaExecution, checklistId: string): QaAreaExecution {
  if (current.checklists.some((checklist) => checklist.id === checklistId)) return current
  const index = saved.checklists.findIndex((checklist) => checklist.id === checklistId)
  const checklist = saved.checklists[index]
  if (!checklist) return current
  const checklists = [...current.checklists]
  checklists.splice(Math.max(0, index), 0, checklist)
  return removeItems({ ...current, checklists }, [])
}

export async function applyQaDelete(input: {
  api: Pick<QaApi, 'deleteItem' | 'deleteChecklist' | 'deleteArea'>
  queryClient: QueryClient
  inspectionId: string | null
  executions: readonly QaAreaExecution[]
  targets: QaDeleteTargets
}): Promise<QaDeleteResult> {
  const { itemArea, checklistArea } = mapsOf(input.executions)
  const affected = new Set<string>(input.targets.areas)
  for (const id of input.targets.items) {
    const areaId = itemArea.get(id)
    if (areaId) affected.add(areaId)
  }
  for (const id of input.targets.checklists) {
    const areaId = checklistArea.get(id)
    if (areaId) affected.add(areaId)
  }
  const affectedAreaIds = [...affected]
  for (const id of affectedAreaIds) {
    await input.queryClient.cancelQueries({ queryKey: ['qa-area-exec', id] })
    holdQaArea(id)
  }
  const areasKey = ['qa-areas', input.inspectionId] as const
  const floorsKey = ['qa-floors', input.inspectionId] as const
  await input.queryClient.cancelQueries({ queryKey: areasKey })
  await input.queryClient.cancelQueries({ queryKey: floorsKey })
  const execSnapshots = new Map<string, QaAreaExecution | undefined>()
  for (const id of affectedAreaIds) {
    execSnapshots.set(id, input.queryClient.getQueryData<QaAreaExecution>(['qa-area-exec', id]))
  }
  const areasSnapshot = input.queryClient.getQueryData(areasKey)
  const floorsSnapshot = input.queryClient.getQueryData(floorsKey)

  const restoreItem = (id: string) => {
    const areaId = itemArea.get(id)
    const saved = areaId ? execSnapshots.get(areaId) : undefined
    if (!areaId || !saved) return
    input.queryClient.setQueryData<QaAreaExecution>(['qa-area-exec', areaId], (current) => (
      current ? putItemBack(current, saved, id) : saved
    ))
  }
  const restoreChecklist = (id: string) => {
    const areaId = checklistArea.get(id)
    const saved = areaId ? execSnapshots.get(areaId) : undefined
    if (!areaId || !saved) return
    input.queryClient.setQueryData<QaAreaExecution>(['qa-area-exec', areaId], (current) => (
      current ? putChecklistBack(current, saved, id) : saved
    ))
  }
  const restoreArea = (areaId: string) => {
    // The value stored for this area before the click, not the bundle of every key.
    input.queryClient.setQueryData(['qa-area-exec', areaId], execSnapshots.get(areaId))
    const area = rowsOf<AreaRow>(areasSnapshot).find((row) => row.id === areaId)
    if (area) {
      input.queryClient.setQueryData(areasKey, (current: unknown) => mapCachedList<AreaRow>(current, (rows) => (
        rows.some((row) => row.id === areaId) ? rows : [...rows, area]
      )))
    }
    const savedFloor = rowsOf<FloorRow>(floorsSnapshot).find((floor) => floor.areas?.some((row) => row.id === areaId))
    const nested = savedFloor?.areas?.find((row) => row.id === areaId)
    if (!savedFloor || !nested) return
    input.queryClient.setQueryData(floorsKey, (current: unknown) => mapCachedList<FloorRow>(current, (floors) => (
      floors.map((floor) => {
        if (floor.id !== savedFloor.id) return floor
        const areas = Array.isArray(floor.areas) ? floor.areas : []
        return areas.some((row) => row.id === areaId) ? floor : { ...floor, areas: [...areas, nested] }
      })
    )))
  }

  try {
    for (const areaId of affectedAreaIds) {
      const itemIds = input.targets.items.filter((id) => itemArea.get(id) === areaId)
      const checklistIds = input.targets.checklists.filter((id) => checklistArea.get(id) === areaId)
      input.queryClient.setQueryData<QaAreaExecution>(['qa-area-exec', areaId], (current) => {
        if (!current) return current
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (removeItems)
        let next = removeItems(current, itemIds)
        for (const id of checklistIds) {
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (removeChecklist)
          next = removeChecklist(next, id)
        }
        return next
      })
    }
    for (const areaId of input.targets.areas) {
      input.queryClient.setQueryData(areasKey, (current: unknown) => (
        mapCachedList<AreaRow>(current, (rows) => rows.filter((row) => row.id !== areaId))
      ))
      input.queryClient.setQueryData(floorsKey, (current: unknown) => (
        mapCachedList<FloorRow>(current, (floors) => floors.map((floor) => ({
          ...floor,
          areas: Array.isArray(floor.areas) ? floor.areas.filter((row) => row.id !== areaId) : floor.areas,
        })))
      ))
    }

    const itemResults = await Promise.allSettled(input.targets.items.map((id) => input.api.deleteItem(id)))
    const checklistResults = await Promise.allSettled(input.targets.checklists.map((id) => input.api.deleteChecklist(id)))
    const areaResults = await Promise.allSettled(input.targets.areas.map((id) => input.api.deleteArea(id)))
    const deletedItems: string[] = []
    const pendingItems: string[] = []
    const deletedChecklists: string[] = []
    const deletedAreas: string[] = []
    let failed = 0

    input.targets.items.forEach((id, index) => {
      const result = itemResults[index]
      if (!result || result.status === 'rejected') {
        failed += 1
        restoreItem(id)
        return
      }
      if (isPendingQaChange(result.value)) {
        pendingItems.push(id)
        restoreItem(id)
        return
      }
      deletedItems.push(id)
    })
    input.targets.checklists.forEach((id, index) => {
      const result = checklistResults[index]
      if (!result || result.status === 'rejected') {
        failed += 1
        restoreChecklist(id)
        return
      }
      deletedChecklists.push(id)
    })
    input.targets.areas.forEach((id, index) => {
      const result = areaResults[index]
      if (!result || result.status === 'rejected') {
        failed += 1
        restoreArea(id)
        return
      }
      deletedAreas.push(id)
    })
    for (const areaId of deletedAreas) {
      input.queryClient.removeQueries({ queryKey: ['qa-area-exec', areaId], exact: true })
    }
    return { deletedItems, pendingItems, deletedChecklists, deletedAreas, failed }
  } finally {
    for (const id of affectedAreaIds) releaseQaArea(id)
  }
}

export async function applyQaAssign(input: {
  queryClient: QueryClient
  inspectionId: string | null
  executions: readonly QaAreaExecution[]
  areaIds: readonly string[]
  itemIds: readonly string[]
  person: { userId: number; name: string; dueDate: string; email?: string | null }
}): Promise<{ release: () => void; restore: () => void }> {
  const areaIds = [...input.areaIds]
  const itemIds = new Set(input.itemIds)
  const touched = new Set<string>(areaIds)
  for (const area of input.executions) {
    if (area.checklists.some((checklist) => checklist.items.some((item) => itemIds.has(item.id)))) {
      touched.add(area.id)
    }
  }
  for (const id of touched) {
    await input.queryClient.cancelQueries({ queryKey: ['qa-area-exec', id] })
    holdQaArea(id)
  }
  const areasKey = ['qa-areas', input.inspectionId] as const
  const floorsKey = ['qa-floors', input.inspectionId] as const
  const areasSnapshot = input.queryClient.getQueryData(areasKey)
  const floorsSnapshot = input.queryClient.getQueryData(floorsKey)
  const execSnapshots = new Map<string, unknown>()
  for (const id of touched) execSnapshots.set(id, input.queryClient.getQueryData(['qa-area-exec', id]))
  const person = input.person
  input.queryClient.setQueryData(areasKey, (current: unknown) => mapCachedList<AreaRow>(current, (rows) => (
    rows.map((row) => (areaIds.includes(row.id) ? withAssignee(row, person) : row))
  )))
  input.queryClient.setQueryData(floorsKey, (current: unknown) => mapCachedList<FloorRow>(current, (floors) => (
    floors.map((floor) => ({
      ...floor,
      areas: Array.isArray(floor.areas)
        ? floor.areas.map((row) => (areaIds.includes(row.id) ? withAssignee(row, person) : row))
        : floor.areas,
    }))
  )))
  for (const id of touched) {
    input.queryClient.setQueryData<QaAreaExecution>(['qa-area-exec', id], (current) => {
      if (!current) return current
      const next = areaIds.includes(id) ? withAssignee(current, person) : current
      return {
        ...next,
        checklists: next.checklists.map((checklist) => ({
          ...checklist,
          items: checklist.items.map((item) => (itemIds.has(item.id) ? withAssignee(item, person) : item)),
        })),
      }
    })
  }
  return {
    release: () => { for (const id of touched) releaseQaArea(id) },
    restore: () => {
      input.queryClient.setQueryData(areasKey, areasSnapshot)
      input.queryClient.setQueryData(floorsKey, floorsSnapshot)
      for (const [id, snapshot] of execSnapshots) {
        input.queryClient.setQueryData(['qa-area-exec', id], snapshot)
      }
    },
  }
}

export function placeQaMove(queryClient: QueryClient, inspectionId: string | null, result: QaMoveResult): void {
  const areasKey = ['qa-areas', inspectionId] as const
  const floorsKey = ['qa-floors', inspectionId] as const
  if (result.node_type === 'checklist_instance') {
    for (const move of result.moves) {
      let checklist: QaChecklistInstance | undefined
      queryClient.setQueryData<QaAreaExecution>(['qa-area-exec', move.from_parent_id], (current) => {
        checklist = current?.checklists.find((row) => row.id === move.id)
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (removeChecklist)
        return current ? removeChecklist(current, move.id) : current
      })
      if (!checklist) continue
      queryClient.setQueryData<QaAreaExecution>(['qa-area-exec', move.to_parent_id], (current) => (
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (appendChecklist)
        current ? appendChecklist(current, checklist as QaChecklistInstance) : current
      ))
    }
    return
  }
  if (result.node_type !== 'area') return
  const movedIds = new Set(result.moves.map((move) => move.id))
  queryClient.setQueryData(areasKey, (current: unknown) => mapCachedList<AreaRow>(current, (rows) => (
    rows.map((row) => {
      const move = result.moves.find((entry) => entry.id === row.id)
      return move ? { ...row, floor: move.to_parent_id, sort_order: move.sort_order } : row
    })
  )))
  queryClient.setQueryData(floorsKey, (current: unknown) => mapCachedList<FloorRow>(current, (floors) => {
    const loose = floors.flatMap((floor) => floor.areas ?? []).filter((row) => movedIds.has(row.id))
    const areas = rowsOf<AreaRow>(queryClient.getQueryData(areasKey))
    let next = floors.map((floor) => ({
      ...floor,
      areas: (floor.areas ?? []).filter((row) => !movedIds.has(row.id)),
    }))
    if (result.created_parent && !next.some((floor) => floor.id === result.parent_id)) {
      next = [...next, { id: result.parent_id, inspection: inspectionId ?? undefined, floor_name: result.parent_name, areas: [] }]
    }
    next = next.map((floor) => {
      const additions = result.moves
        .filter((move) => move.to_parent_id === floor.id)
        .map((move) => {
          const fromAreas = areas.find((row) => row.id === move.id)
          const fromFloor = loose.find((row) => row.id === move.id)
          const base = fromAreas ?? fromFloor
          return base ? { ...base, floor: move.to_parent_id, sort_order: move.sort_order } : { id: move.id, floor: move.to_parent_id, sort_order: move.sort_order }
        })
      return { ...floor, areas: [...(floor.areas ?? []), ...additions] }
    })
    if (result.parent_id !== 'pending-level' && next.some((floor) => floor.id === result.parent_id)) {
      next = next.filter((floor) => floor.id !== 'pending-level')
    }
    return next
  }))
}

export type { QaChecklistInstance, QaItemInstance }
